import type {
  IEventBus,
  IEventBusSubscribes,
  EventBusCallback,
  EventBusCallbackStored,
  EventBusSubscribeMap,
} from './eventBus.types'

const subscribers: IEventBusSubscribes = new Map()

export class EventBus implements IEventBus {
  protected subscribers

  constructor() {
    this.subscribers = subscribers
  }

  protected nextId(): symbol {
    return Symbol('')
  }

  protected subscriberGet(name: string): EventBusSubscribeMap {
    let subscriber = this.subscribers.get(name)
    if (subscriber) return subscriber
    subscriber = new Map()
    this.subscribers.set(name, subscriber)
    return subscriber
  }

  public on<P>(name: string, callback: EventBusCallback<P>): void {
    const subscriber = this.subscriberGet(name)
    const id = this.nextId()
    subscriber.set(id, callback as EventBusCallbackStored)
  }

  public off<P>(name: string, callback?: EventBusCallback<P>): void {
    if (!this.subscribers.has(name)) return
    const subscriber = this.subscriberGet(name)
    if (callback) {
      subscriber.forEach((cb, id) => {
        if (cb === (callback as EventBusCallbackStored)) subscriber.delete(id)
      })
    } else {
      this.subscribers.delete(name)
    }
  }

  public one<P>(name: string, callback?: EventBusCallback<P>): void {
    const subscriber = this.subscriberGet(name)
    const id = this.nextId()
    const callbackOne = (payload: P) => {
      this.off(name, callbackOne)
      callback?.(payload)
    }
    subscriber.set(id, callbackOne as EventBusCallbackStored)
  }

  public emit<T>(name: string, payload?: T): void {
    if (!this.subscribers.has(name)) return
    const subscriber = this.subscriberGet(name)
    subscriber.forEach((cb) => (cb as EventBusCallback<T | undefined>)(payload))
  }

  public nameGenerate(context: string, target: string, command: string): string {
    return `${context}:${target}:${command}`
  }
}

export const eventBus = new EventBus()
